import type { FeaturedVideo } from '@/shared/types';

/**
 * Group the showcase feed by how each video was RENDERED, not by what it is about.
 *
 * The landing page needs sections on day one, and `featured_videos.category` is
 * curator-assigned — empty until somebody fills it in, which on a fresh catalogue
 * means one undifferentiated wall. `jobs.style` is set by the pipeline that
 * produced every job, so it always partitions the feed.
 *
 * The trade is that these labels describe the technique rather than the story:
 * "Storybook" tells a visitor how it was drawn, not what happens in it. That is
 * honest and needs no curation, but it is the weaker pitch — the day `category` is
 * populated, group by that instead and keep this for the day it is not.
 */

/** Display order and copy. A style not named here falls into no section. */
const STYLE_SECTIONS: { style: string; heading: string; blurb: string }[] = [
  {
    style: 'storybook',
    heading: 'Storybook',
    blurb: 'Illustrated worlds, drawn frame by frame.',
  },
  {
    style: 'photoreal',
    heading: 'Photoreal',
    blurb: 'Live-action scenes, rendered from the page.',
  },
  {
    /* "Research and Learning", not a `research` style of its own.

       The paper-explainer feature renders arXiv papers at `documentation` style --
       there is no separate value in the engine's `VideoStyle`, and adding one would
       mean a new render treatment rather than a new label.

       The two other headings name a rendering TECHNIQUE, which is what actually
       groups these bands. This one names the subject instead, and it is longer than
       both. Deliberate: "Documentation" describes how the video was made and tells a
       visitor nothing they care about, and the band covers papers, docs and
       tutorials alike -- "Research" alone reads as academic-only.

       The band stays hidden until the first such video is featured, per the filter
       below. That is the intended behaviour, not a gap: it appears on its own the
       day there is something to put in it. */
    style: 'documentation',
    heading: 'Research and Learning',
    blurb: 'Papers and technical writing, explained frame by frame.',
  },
];

/** Every style that has a section, for route matching. */
export const SHOWCASE_STYLES = STYLE_SECTIONS.map((section) => section.style);

/**
 * Heading and blurb for one style, for the "see all" page.
 *
 * Shared with the bands rather than duplicated there: the page a band links to has
 * to be titled the same as the band, and two copies of "Storybook" drift the first
 * time one is reworded.
 */
export function styleCopy(style: string): { heading: string; blurb: string } {
  const found = STYLE_SECTIONS.find((section) => section.style === style);
  /* A style with no entry here is reachable by typing the URL. Falling back to the
     slug keeps the page coherent rather than blank-titled -- the route guard sends
     genuinely unknown styles home, so this only covers a style added to the enum
     before it was added above. */
  return found ?? { heading: style, blurb: '' };
}

export type StyleSection = {
  key: string;
  heading: string;
  blurb: string;
  videos: FeaturedVideo[];
};

export function buildStyleSections(videos: FeaturedVideo[]): StyleSection[] {
  if (videos.length === 0) return [];

  const byStyle = new Map<string, FeaturedVideo[]>();
  for (const video of videos) {
    if (!video.style) continue;
    const list = byStyle.get(video.style);
    if (list) list.push(video);
    else byStyle.set(video.style, [video]);
  }

  /* Only sections that have something in them. An empty "Research and Learning"
     heading over blank space advertises a gap rather than a catalogue -- so the band
     appears by itself once such a video is featured, with no further change here. */
  return STYLE_SECTIONS.flatMap(({ style, heading, blurb }) => {
    const found = byStyle.get(style);
    return found?.length ? [{ key: style, heading, blurb, videos: found }] : [];
  });
}
